/**
 * useAssessment Hook
 *
 * Custom React hook for managing HVAC assessment state across 6-step wizard.
 * Uses reducer pattern for complex state management.
 *
 * @module useAssessment
 */

import { useReducer, useCallback, useMemo, useEffect } from 'react';
import { analyzeSystem, estimateEnergyCost, validateSystemData } from '../utils/calculatorFunctions';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { detectClimateFromZip, getClimateData } from '../utils/climateZones';

/**
 * Initial state for assessment form
 */
const initialState = {
  // Current wizard step (1-6)
  currentStep: 1,

  // Step 1: Customer Information & Property Address
  customerFirstName: '',
  customerLastName: '',
  propertyStreet: '',
  propertyCity: '',
  propertyCounty: '',
  propertyState: '',
  propertyZip: '',

  // Climate zone data
  contractorClimateZone: null,
  customerClimateZone: null,

  // Step 2: Location (Jurisdiction & Utility Providers)
  jurisdictionIds: [],
  availableJurisdictions: [],
  electricProviderId: null,
  gasProviderId: null,
  hasGasOrPropane: false,
  electricProviders: [],
  gasProviders: [],
  electricRate: null, // $/kWh
  gasRate: null, // $/therm

  // Step 3: System Information
  systemAge: 8,
  repairCost: 0,
  systemType: '',
  squareFootage: 2000,
  current_afue: null,  // For gas systems: current AFUE rating (60-98)
  can_install_condensing: null,  // For gas systems: 'yes', 'no', or 'unsure'

  // Step 4: Maintenance & Technical
  lastRepair: 'never',
  lastRepairCost: 0,
  refrigerant: '',
  originalSeer: null,
  ductCondition: 'good',
  convertToHP: false,

  // Step 4: Home Envelope (optional section)
  envelope: {
    airTightness: 'average',
    insulation: 'average',
    windows: 'double'
  },

  // Step 5: Comfort & Performance
  issues: {
    hotCold: false,
    humidity: false,
    runsConstantly: false,
    highBills: false,
    loud: false,
    tempTrouble: false
  },
  annualEnergyCost: 600,

  // Rebates (automatically matched in results)
  availableRebates: [],
  selectedRebates: [],

  // Enabled system types (from Settings)
  enabledSystemTypes: [],

  // Analysis results (populated after calculation)
  results: null,

  // Saved assessment info
  assessmentId: null,
  customerId: null,

  // AI summary
  aiSummary: null,
  isGeneratingAI: false,

  // Validation errors
  errors: {},

  // Loading states
  isCalculating: false,
  isSaving: false
};

/**
 * Action types for reducer
 */
const ActionTypes = {
  SET_FIELD: 'SET_FIELD',
  SET_ENVELOPE_FIELD: 'SET_ENVELOPE_FIELD',
  SET_ISSUE: 'SET_ISSUE',
  SET_PROVIDERS: 'SET_PROVIDERS',
  SET_UTILITY_RATES: 'SET_UTILITY_RATES',
  SET_JURISDICTIONS: 'SET_JURISDICTIONS',
  SET_REBATES: 'SET_REBATES',
  SET_ENABLED_SYSTEM_TYPES: 'SET_ENABLED_SYSTEM_TYPES',
  SET_CONTRACTOR_CLIMATE_ZONE: 'SET_CONTRACTOR_CLIMATE_ZONE',
  SET_CUSTOMER_CLIMATE_ZONE: 'SET_CUSTOMER_CLIMATE_ZONE',
  NEXT_STEP: 'NEXT_STEP',
  PREV_STEP: 'PREV_STEP',
  GO_TO_STEP: 'GO_TO_STEP',
  SET_ERRORS: 'SET_ERRORS',
  START_CALCULATION: 'START_CALCULATION',
  SET_RESULTS: 'SET_RESULTS',
  START_SAVING: 'START_SAVING',
  SAVE_SUCCESS: 'SAVE_SUCCESS',
  SAVE_ERROR: 'SAVE_ERROR',
  START_AI_GENERATION: 'START_AI_GENERATION',
  AI_GENERATION_SUCCESS: 'AI_GENERATION_SUCCESS',
  AI_GENERATION_ERROR: 'AI_GENERATION_ERROR',
  RESET: 'RESET',
  UPDATE_ENERGY_COST: 'UPDATE_ENERGY_COST'
};

/**
 * Reducer function for assessment state management
 */
function assessmentReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_FIELD:
      const newState = {
        ...state,
        [action.field]: action.value,
        errors: {
          ...state.errors,
          [action.field]: undefined // Clear error for this field
        }
      };

      // If hasGasOrPropane is unchecked or gasProviderId is cleared,
      // reset systemType if it's a gas-dependent type
      if (action.field === 'hasGasOrPropane' && !action.value) {
        const gasSystemTypes = ['furnaceAC', 'furnaceOnly', 'boiler'];
        if (gasSystemTypes.includes(newState.systemType)) {
          newState.systemType = 'centralAC'; // Reset to default
        }
      }

      if (action.field === 'gasProviderId' && !action.value && state.hasGasOrPropane) {
        const gasSystemTypes = ['furnaceAC', 'furnaceOnly', 'boiler'];
        if (gasSystemTypes.includes(newState.systemType)) {
          newState.systemType = 'centralAC'; // Reset to default
        }
      }

      return newState;

    case ActionTypes.SET_ENVELOPE_FIELD:
      return {
        ...state,
        envelope: {
          ...state.envelope,
          [action.field]: action.value
        }
      };

    case ActionTypes.SET_ISSUE:
      return {
        ...state,
        issues: {
          ...state.issues,
          [action.issue]: action.value
        }
      };

    case ActionTypes.SET_PROVIDERS:
      return {
        ...state,
        electricProviders: action.payload.electric,
        gasProviders: action.payload.gas
      };

    case ActionTypes.SET_UTILITY_RATES:
      return {
        ...state,
        electricRate: action.electricRate,
        gasRate: action.gasRate
      };

    case ActionTypes.SET_JURISDICTIONS:
      return {
        ...state,
        availableJurisdictions: action.jurisdictions
      };

    case ActionTypes.SET_REBATES:
      return {
        ...state,
        availableRebates: action.rebates
      };

    case ActionTypes.SET_ENABLED_SYSTEM_TYPES:
      return {
        ...state,
        enabledSystemTypes: action.systemTypes
      };

    case ActionTypes.SET_CONTRACTOR_CLIMATE_ZONE:
      return {
        ...state,
        contractorClimateZone: action.climateZone
      };

    case ActionTypes.SET_CUSTOMER_CLIMATE_ZONE:
      return {
        ...state,
        customerClimateZone: action.climateZone
      };

    case ActionTypes.NEXT_STEP:
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, 6)
      };

    case ActionTypes.PREV_STEP:
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 1)
      };

    case ActionTypes.GO_TO_STEP:
      return {
        ...state,
        currentStep: action.step
      };

    case ActionTypes.SET_ERRORS:
      return {
        ...state,
        errors: action.errors
      };

    case ActionTypes.START_CALCULATION:
      return {
        ...state,
        isCalculating: true,
        results: null
      };

    case ActionTypes.SET_RESULTS:
      return {
        ...state,
        isCalculating: false,
        results: action.results
      };

    case ActionTypes.UPDATE_ENERGY_COST:
      // Ensure value is valid and within acceptable range
      let validCost = action.value;
      if (isNaN(validCost) || validCost === null || validCost === undefined || validCost < 100) {
        console.warn('Invalid energy cost estimate:', action.value, '- using default 600');
        validCost = 600; // Safe default
      }
      return {
        ...state,
        annualEnergyCost: validCost
      };

    case ActionTypes.START_SAVING:
      return {
        ...state,
        isSaving: true,
        errors: { ...state.errors, save: undefined }
      };

    case ActionTypes.SAVE_SUCCESS:
      return {
        ...state,
        isSaving: false,
        assessmentId: action.assessmentId,
        customerId: action.customerId
      };

    case ActionTypes.SAVE_ERROR:
      return {
        ...state,
        isSaving: false,
        errors: {
          ...state.errors,
          save: action.error
        }
      };

    case ActionTypes.START_AI_GENERATION:
      return {
        ...state,
        isGeneratingAI: true,
        errors: { ...state.errors, ai: undefined }
      };

    case ActionTypes.AI_GENERATION_SUCCESS:
      return {
        ...state,
        isGeneratingAI: false,
        aiSummary: action.summary
      };

    case ActionTypes.AI_GENERATION_ERROR:
      return {
        ...state,
        isGeneratingAI: false,
        errors: {
          ...state.errors,
          ai: action.error
        }
      };

    case ActionTypes.RESET:
      return initialState;

    default:
      return state;
  }
}

/**
 * Validation rules for each step
 */
const stepValidations = {
  1: (state) => {
    const errors = {};

    // First name validation (required)
    if (!state.customerFirstName || state.customerFirstName.trim() === '') {
      errors.customerFirstName = 'First name is required';
    }

    // Last name validation (required)
    if (!state.customerLastName || state.customerLastName.trim() === '') {
      errors.customerLastName = 'Last name is required';
    }

    // Property street validation (required)
    if (!state.propertyStreet || state.propertyStreet.trim() === '') {
      errors.propertyStreet = 'Property street address is required';
    }

    // Property city validation (required)
    if (!state.propertyCity || state.propertyCity.trim() === '') {
      errors.propertyCity = 'City is required';
    }

    // Property state validation (required)
    if (!state.propertyState || state.propertyState.trim() === '') {
      errors.propertyState = 'State is required';
    }

    // Property zip validation (required)
    if (!state.propertyZip || state.propertyZip.trim() === '') {
      errors.propertyZip = 'Zip code is required';
    } else if (!/^\d{5}$/.test(state.propertyZip)) {
      errors.propertyZip = 'Zip code must be 5 digits';
    }

    // Note: Climate zone validation removed to allow contractors to work
    // anywhere in their licensed state. Many states (e.g., California) span
    // multiple climate zones, so zone-based restrictions are too restrictive.
    // State-level licensing is enforced in Step 2 (Geography/Jurisdictions).

    return errors;
  },

  2: (state) => {
    const errors = {};

    // County jurisdiction is required (city is optional)
    const hasCountySelected = state.jurisdictionIds.some(id => {
      const jurisdiction = state.availableJurisdictions.find(j => j.id === id);
      return jurisdiction && jurisdiction.jurisdiction_type === 'county';
    });

    if (!hasCountySelected) {
      errors.jurisdictionIds = 'Please select a county';
    }

    // Electric provider validation (required)
    if (!state.electricProviderId) {
      errors.electricProviderId = 'Please select an electricity provider';
    }

    // Gas provider required only if checkbox is checked
    if (state.hasGasOrPropane && !state.gasProviderId) {
      errors.gasProviderId = 'Please select a gas/propane provider';
    }

    return errors;
  },

  3: (state) => {
    const errors = {};

    // System age validation
    if (state.systemAge < 0 || state.systemAge > 30) {
      errors.systemAge = 'System age must be between 0 and 30 years';
    }

    // Repair cost validation
    if (state.repairCost < 0) {
      errors.repairCost = 'Repair cost cannot be negative';
    }

    if (state.repairCost === null || state.repairCost === undefined || state.repairCost === '') {
      errors.repairCost = 'Please enter repair cost (enter 0 if no repair is needed)';
    }

    // System type validation
    if (!state.systemType || state.systemType === '') {
      errors.systemType = 'Please select a system type';
    }

    // Square footage validation
    if (state.squareFootage < 500 || state.squareFootage > 10000) {
      errors.squareFootage = 'Square footage must be between 500 and 10,000';
    }

    // Gas system specific validations
    const gasSystemTypes = ['furnaceAC', 'furnaceACPackage', 'furnaceOnly', 'boiler'];
    if (gasSystemTypes.includes(state.systemType)) {
      // Current AFUE validation (optional, but if provided must be valid)
      if (state.current_afue !== null && state.current_afue !== undefined && state.current_afue !== '') {
        const afueNum = parseInt(state.current_afue);
        if (isNaN(afueNum) || afueNum < 60 || afueNum > 98) {
          errors.current_afue = 'AFUE must be between 60 and 98';
        }
      }

      // Condensing capability validation - not required, but helpful for filtering options
      // No validation error needed - it's optional and defaults to 'unsure' behavior
    }

    return errors;
  },

  4: (state) => {
    const errors = {};

    // Last repair cost validation (only if they had a repair)
    if (state.lastRepair !== 'never') {
      if (state.lastRepairCost < 0) {
        errors.lastRepairCost = 'Last repair cost cannot be negative';
      }
    }

    // Refrigerant type validation
    if (!state.refrigerant || state.refrigerant === '') {
      errors.refrigerant = 'Please select a refrigerant type';
    }

    // Original SEER validation (if provided)
    if (state.originalSeer !== null && state.originalSeer !== '') {
      const seer = parseFloat(state.originalSeer);
      if (isNaN(seer) || seer < 6 || seer > 30) {
        errors.originalSeer = 'SEER rating must be between 6 and 30';
      }
    }

    return errors;
  },

  5: (state) => {
    const errors = {};

    // Annual energy cost validation - higher limits for heat pumps and ductless
    const maxLimit = (state.systemType === 'heatPump' || state.systemType === 'heatPumpPackage' || state.systemType === 'ductlessMini')
      ? 25000  // Heat pumps/ductless with both heating and cooling can be expensive
      : (state.electricRate > 0.20 ? 20000 : 15000);  // Higher limits for expensive electric rates

    if (state.annualEnergyCost < 100 || state.annualEnergyCost > maxLimit) {
      errors.annualEnergyCost = `Annual energy cost must be between $100 and $${maxLimit.toLocaleString()}`;
    }

    return errors;
  }
};

/**
 * Main hook for assessment state management
 * @returns {Object} Assessment state and methods
 */
export function useAssessment() {
  const [state, dispatch] = useReducer(assessmentReducer, initialState);
  const { user, refreshContractor } = useAuth();

  /**
   * Load contractor climate zone on mount
   */
  useEffect(() => {
    const loadContractorClimateZone = async () => {
      if (!user?.contractor_id) return;

      try {
        const { data, error } = await supabase
          .from('contractors')
          .select('climate_zone')
          .eq('id', user.contractor_id)
          .single();

        if (error) throw error;

        if (data?.climate_zone) {
          dispatch({
            type: ActionTypes.SET_CONTRACTOR_CLIMATE_ZONE,
            climateZone: data.climate_zone
          });
        }
      } catch (error) {
        console.error('Error loading contractor climate zone:', error);
      }
    };

    loadContractorClimateZone();
  }, [user?.contractor_id]);

  /**
   * Load utility providers on mount
   */
  useEffect(() => {
    const loadUtilityProviders = async () => {
      if (!user?.contractor_id) return;

      try {
        const { data: providers, error } = await supabase
          .from('utility_providers')
          .select('*')
          .eq('contractor_id', user.contractor_id)
          .order('provider_name');

        if (error) throw error;

        if (providers) {
          const electric = providers.filter(p => p.utility_type === 'electric');
          const gas = providers.filter(p => p.utility_type === 'gas' || p.utility_type === 'propane');

          dispatch({
            type: ActionTypes.SET_PROVIDERS,
            payload: { electric, gas }
          });

          console.log('Loaded utility providers:', { electric: electric.length, gas: gas.length });
        }
      } catch (error) {
        console.error('Error loading utility providers:', error);
      }
    };

    loadUtilityProviders();
  }, [user?.contractor_id]);

  /**
   * Load utility rates when providers are selected
   */
  useEffect(() => {
    const loadUtilityRates = async () => {
      let electricRate = null;
      let gasRate = null;

      // Fetch electric rate if provider is selected
      if (state.electricProviderId) {
        const provider = state.electricProviders.find(p => p.id === state.electricProviderId);
        if (provider) {
          electricRate = provider.rate; // $/kWh
        }
      }

      // Fetch gas rate if provider is selected
      if (state.gasProviderId) {
        const provider = state.gasProviders.find(p => p.id === state.gasProviderId);
        if (provider) {
          gasRate = provider.rate; // $/therm
        }
      }

      dispatch({
        type: ActionTypes.SET_UTILITY_RATES,
        electricRate,
        gasRate
      });
    };

    loadUtilityRates();
  }, [state.electricProviderId, state.gasProviderId, state.electricProviders, state.gasProviders]);

  /**
   * Load geographic jurisdictions on mount (Phase 16B)
   */
  useEffect(() => {
    const loadJurisdictions = async () => {
      if (!user?.contractor_id) return;

      try {
        const { data: jurisdictions, error } = await supabase
          .from('geographic_jurisdictions')
          .select('*')
          .eq('contractor_id', user.contractor_id)
          .order('jurisdiction_type', { ascending: true })
          .order('jurisdiction_name');

        if (error) throw error;

        if (jurisdictions) {
          dispatch({
            type: ActionTypes.SET_JURISDICTIONS,
            jurisdictions: jurisdictions
          });
        }
      } catch (error) {
        console.error('Error loading jurisdictions:', error);
      }
    };

    loadJurisdictions();
  }, [user?.contractor_id]);

  /**
   * Load active rebates on mount (Phase 16)
   */
  useEffect(() => {
    const loadActiveRebates = async () => {
      if (!user?.contractor_id) return;

      try {
        const today = new Date().toISOString().split('T')[0];

        const { data: rebates, error } = await supabase
          .from('rebates')
          .select('*')
          .eq('contractor_id', user.contractor_id)
          .eq('status', 'active')
          .lte('start_date', today)
          .gte('end_date', today)
          .order('display_priority', { ascending: true })
          .order('end_date', { ascending: true })
          .order('name');

        if (error) throw error;

        if (rebates) {
          dispatch({
            type: ActionTypes.SET_REBATES,
            rebates: rebates
          });
        }
      } catch (error) {
        console.error('Error loading active rebates:', error);
      }
    };

    loadActiveRebates();
  }, [user?.contractor_id]);

  /**
   * Load enabled system types on mount
   */
  useEffect(() => {
    const loadEnabledSystemTypes = async () => {
      if (!user?.contractor_id) return;

      try {
        const { data: settings, error } = await supabase
          .from('system_type_settings')
          .select('system_type, is_enabled')
          .eq('contractor_id', user.contractor_id)
          .eq('is_enabled', true);

        if (error) throw error;

        // Only return explicitly enabled types
        // If no settings exist, return empty array (forces contractor to enable systems in Settings)
        const enabledTypes = settings ? settings.map(s => s.system_type) : [];

        dispatch({
          type: ActionTypes.SET_ENABLED_SYSTEM_TYPES,
          systemTypes: enabledTypes
        });
      } catch (error) {
        console.error('Error loading enabled system types:', error);
        // On error, return empty array (safe default - contractor must enable systems)
        dispatch({
          type: ActionTypes.SET_ENABLED_SYSTEM_TYPES,
          systemTypes: []
        });
      }
    };

    loadEnabledSystemTypes();
  }, [user?.contractor_id]);

  /**
   * Set a field value
   */
  const setField = useCallback((field, value) => {
    dispatch({ type: ActionTypes.SET_FIELD, field, value });
  }, []);

  /**
   * Set an envelope field value
   */
  const setEnvelopeField = useCallback((field, value) => {
    dispatch({ type: ActionTypes.SET_ENVELOPE_FIELD, field, value });
  }, []);

  /**
   * Set a comfort issue
   */
  const setIssue = useCallback((issue, value) => {
    dispatch({ type: ActionTypes.SET_ISSUE, issue, value });
  }, []);

  /**
   * Toggle a comfort issue
   */
  const toggleIssue = useCallback((issue) => {
    dispatch({ type: ActionTypes.SET_ISSUE, issue, value: !state.issues[issue] });
  }, [state.issues]);

  /**
   * Validate current step
   */
  const validateCurrentStep = useCallback(() => {
    const validator = stepValidations[state.currentStep];
    if (validator) {
      const errors = validator(state);
      dispatch({ type: ActionTypes.SET_ERRORS, errors });
      return Object.keys(errors).length === 0;
    }
    return true;
  }, [state]);

  /**
   * Go to next step
   */
  const nextStep = useCallback(() => {
    if (validateCurrentStep()) {
      dispatch({ type: ActionTypes.NEXT_STEP });
      // Scroll to top when changing steps
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return true;
    }
    return false;
  }, [validateCurrentStep]);

  /**
   * Go to previous step
   */
  const prevStep = useCallback(() => {
    dispatch({ type: ActionTypes.PREV_STEP });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /**
   * Go to specific step (with validation of current step)
   */
  const goToStep = useCallback((step) => {
    // Only allow going to a step if current step is valid
    // or if going backwards
    if (step < state.currentStep || validateCurrentStep()) {
      dispatch({ type: ActionTypes.GO_TO_STEP, step });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return true;
    }
    return false;
  }, [state.currentStep, validateCurrentStep]);

  /**
   * Auto-calculate energy cost estimate
   */
  const updateEnergyCostEstimate = useCallback(() => {
    try {
      // Get climate data from contractor's climate zone
      const climateZone = state.contractorClimateZone || '4A'; // Default to 4A if not set
      const zoneInfo = getClimateData(climateZone);
      const climateData = {
        zone: climateZone,
        coolingHours: zoneInfo.coolingHours,
        heatingHours: zoneInfo.heatingHours
      };

      const estimate = estimateEnergyCost({
        systemType: state.systemType,
        age: state.systemAge,
        originalSeer: state.originalSeer,
        squareFootage: state.squareFootage,
        ductCondition: state.ductCondition,
        issues: state.issues,
        envelope: state.envelope,
        climateData: climateData,
        electricRate: state.electricRate,
        gasRate: state.gasRate
      });

      console.log('Energy cost estimate calculated:', estimate);
      dispatch({ type: ActionTypes.UPDATE_ENERGY_COST, value: estimate });
    } catch (error) {
      console.error('Error calculating energy cost estimate:', error);
      // Use default value on error
      dispatch({ type: ActionTypes.UPDATE_ENERGY_COST, value: 600 });
    }
  }, [
    state.systemType,
    state.systemAge,
    state.originalSeer,
    state.squareFootage,
    state.ductCondition,
    state.issues,
    state.envelope,
    state.contractorClimateZone,
    state.electricRate,
    state.gasRate
  ]);

  /**
   * Calculate final results
   */
  const calculateResults = useCallback(async () => {
    // Validate all data first
    const validation = validateSystemData({
      age: state.systemAge,
      squareFootage: state.squareFootage,
      repairCost: state.repairCost
    });

    if (!validation.isValid) {
      dispatch({
        type: ActionTypes.SET_ERRORS,
        errors: { general: validation.errors.join(', ') }
      });
      return;
    }

    dispatch({ type: ActionTypes.START_CALCULATION });

    try {
      // Fetch contractor climate settings
      let climateData = {
        coolingHours: 1400, // Default (Zone 4A)
        heatingHours: 2200
      };

      if (user?.contractor_id) {
        const { data: contractorData } = await supabase
          .from('contractors')
          .select('climate_zone, cooling_hours, heating_hours')
          .eq('id', user.contractor_id)
          .single();

        if (contractorData && contractorData.cooling_hours && contractorData.heating_hours) {
          climateData = {
            coolingHours: contractorData.cooling_hours,
            heatingHours: contractorData.heating_hours
          };
        }
      }

      // Fetch utility provider rates (Phase 15)
      let customRates = null;
      if (state.electricProviderId || state.gasProviderId) {
        const providerIds = [state.electricProviderId, state.gasProviderId].filter(Boolean);
        const { data: providers } = await supabase
          .from('utility_providers')
          .select('id, rate, utility_type')
          .in('id', providerIds);

        if (providers) {
          const electricProvider = providers.find(p => p.id === state.electricProviderId);
          const gasProvider = providers.find(p => p.id === state.gasProviderId);

          customRates = {
            electricRate: electricProvider?.rate,
            gasRate: gasProvider?.rate
          };
        }
      }

      // Simulate slight delay for better UX (shows loading state)
      setTimeout(async () => {
        const systemData = {
          age: state.systemAge,
          repairCost: state.repairCost,
          systemType: state.systemType,
          squareFootage: state.squareFootage,
          lastRepair: state.lastRepair,
          lastRepairCost: state.lastRepairCost,
          refrigerant: state.refrigerant,
          originalSeer: state.originalSeer,
          annualEnergyCost: state.annualEnergyCost,
          issues: state.issues,
          ductCondition: state.ductCondition,
          convertToHP: state.convertToHP,
          envelope: state.envelope
        };

        const results = analyzeSystem(systemData, climateData, customRates);
        dispatch({ type: ActionTypes.SET_RESULTS, results });

        // Auto-save assessment immediately after calculating results
        // This ensures billing accuracy by tracking all assessments run
        setTimeout(async () => {
          await saveAssessmentInternal(results, customRates);
        }, 100);
      }, 500);
    } catch (error) {
      console.error('Error fetching climate data:', error);
      // Fall back to default climate zone if there's an error
      setTimeout(async () => {
        const systemData = {
          age: state.systemAge,
          repairCost: state.repairCost,
          systemType: state.systemType,
          squareFootage: state.squareFootage,
          lastRepair: state.lastRepair,
          lastRepairCost: state.lastRepairCost,
          refrigerant: state.refrigerant,
          originalSeer: state.originalSeer,
          annualEnergyCost: state.annualEnergyCost,
          issues: state.issues,
          ductCondition: state.ductCondition,
          convertToHP: state.convertToHP,
          envelope: state.envelope
        };

        const results = analyzeSystem(systemData, null, null);
        dispatch({ type: ActionTypes.SET_RESULTS, results });

        // Auto-save assessment immediately after calculating results
        setTimeout(async () => {
          await saveAssessmentInternal(results, null);
        }, 100);
      }, 500);
    }
  }, [state, user]);

  /**
   * Internal function to save assessment (called automatically after calculation)
   */
  const saveAssessmentInternal = useCallback(async (results, customRates = null) => {
    if (!user) return null;

    try {
      // Step 1: Find or create customer
      let customerId = state.customerId;

      if (!customerId) {
        // Create new customer
        const fullName = `${state.customerFirstName} ${state.customerLastName}`.trim();
        const propertyAddress = `${state.propertyStreet}, ${state.propertyCity}, ${state.propertyState} ${state.propertyZip}`;

        const { data: newCustomer, error: customerError} = await supabase
          .from('customers')
          .insert({
            contractor_id: user.contractor_id,
            full_name: fullName,
            email: null,
            phone: null,
            property_address: propertyAddress
          })
          .select()
          .single();

        if (customerError) throw customerError;
        customerId = newCustomer.id;
      }

      // Step 2: Prepare jurisdiction snapshot
      const jurisdictionSnapshot = (state.jurisdictionIds || []).map(jurisdictionId => {
        const jurisdiction = state.availableJurisdictions.find(j => j.id === jurisdictionId);
        if (!jurisdiction) return null;
        return {
          id: jurisdiction.id,
          jurisdiction_type: jurisdiction.jurisdiction_type,
          jurisdiction_name: jurisdiction.jurisdiction_name,
          parent_jurisdiction: jurisdiction.parent_jurisdiction
        };
      }).filter(Boolean);

      // Prepare rebate snapshot
      const selectedRebatesSnapshot = state.selectedRebates.map(rebateId => {
        const rebate = state.availableRebates.find(r => r.id === rebateId);
        if (!rebate) return null;
        return {
          id: rebate.id,
          name: rebate.name,
          category: rebate.category,
          amount: rebate.amount,
          amount_type: rebate.amount_type,
          description: rebate.description,
          jurisdiction: rebate.jurisdiction,
          utility_provider: rebate.utility_provider,
          start_date: rebate.start_date,
          end_date: rebate.end_date
        };
      }).filter(Boolean);

      // Calculate total rebate savings (fixed amounts only)
      const totalRebateSavings = selectedRebatesSnapshot.reduce((sum, rebate) => {
        if (rebate.amount_type === 'fixed') {
          return sum + parseFloat(rebate.amount);
        }
        return sum;
      }, 0);

      // Step 3: Save assessment
      const systemData = {
        age: state.systemAge,
        repairCost: state.repairCost,
        systemType: state.systemType,
        squareFootage: state.squareFootage,
        lastRepair: state.lastRepair,
        lastRepairCost: state.lastRepairCost,
        refrigerant: state.refrigerant,
        originalSeer: state.originalSeer,
        annualEnergyCost: state.annualEnergyCost,
        issues: state.issues,
        ductCondition: state.ductCondition,
        convertToHP: state.convertToHP,
        envelope: state.envelope,
        results: results
      };

      const { data: assessment, error: assessmentError } = await supabase
        .from('assessments')
        .insert({
          contractor_id: user.contractor_id,
          customer_id: customerId,
          technician_id: user.id,
          system_data: systemData,
          analysis_result: results,
          system_age: state.systemAge,
          repair_cost: state.repairCost,
          recommendation: results.recommendation,
          assessment_score: results.assessmentScore,
          electric_provider_id: state.electricProviderId || null,
          gas_provider_id: state.gasProviderId || null,
          electric_rate: customRates?.electricRate || null,
          gas_rate: customRates?.gasRate || null,
          jurisdiction_ids: state.jurisdictionIds && state.jurisdictionIds.length > 0 ? state.jurisdictionIds : null,
          jurisdiction_snapshot: jurisdictionSnapshot,
          selected_rebates_snapshot: selectedRebatesSnapshot,
          total_rebate_savings: totalRebateSavings
        })
        .select()
        .single();

      if (assessmentError) throw assessmentError;

      // Step 3: Increment contractor assessment count
      const { error: usageError } = await supabase.rpc('increment_assessment_count', {
        p_contractor_id: user.contractor_id
      });

      if (usageError) {
        console.error('Usage tracking error:', usageError);
      } else {
        // Refresh contractor data to update assessment count in Settings page
        if (refreshContractor) {
          await refreshContractor();
        }
      }

      dispatch({
        type: ActionTypes.SAVE_SUCCESS,
        assessmentId: assessment.id,
        customerId: customerId
      });

      return assessment.id;
    } catch (error) {
      console.error('Auto-save assessment error:', error);
      return null;
    }
  }, [state, user, refreshContractor]);

  /**
   * Save assessment to Supabase
   */
  const saveAssessment = useCallback(async () => {
    if (!state.results) {
      dispatch({
        type: ActionTypes.SAVE_ERROR,
        error: 'Please calculate results before saving'
      });
      return null;
    }

    if (!user) {
      dispatch({
        type: ActionTypes.SAVE_ERROR,
        error: 'You must be logged in to save assessments'
      });
      return null;
    }

    dispatch({ type: ActionTypes.START_SAVING });

    try {
      // Step 1: Find or create customer
      let customerId = state.customerId;

      if (!customerId) {
        // Create new customer
        const fullName = `${state.customerFirstName} ${state.customerLastName}`.trim();
        const propertyAddress = `${state.propertyStreet}, ${state.propertyCity}, ${state.propertyState} ${state.propertyZip}`;

        const { data: newCustomer, error: customerError } = await supabase
          .from('customers')
          .insert({
            contractor_id: user.contractor_id,
            full_name: fullName,
            email: null,
            phone: null,
            property_address: propertyAddress
          })
          .select()
          .single();

        if (customerError) throw customerError;
        customerId = newCustomer.id;
      }

      // Step 2: Prepare rebate snapshot (Phase 16)
      const selectedRebatesSnapshot = state.selectedRebates.map(rebateId => {
        const rebate = state.availableRebates.find(r => r.id === rebateId);
        if (!rebate) return null;
        return {
          id: rebate.id,
          name: rebate.name,
          category: rebate.category,
          amount: rebate.amount,
          amount_type: rebate.amount_type,
          description: rebate.description,
          jurisdiction: rebate.jurisdiction,
          utility_provider: rebate.utility_provider,
          start_date: rebate.start_date,
          end_date: rebate.end_date
        };
      }).filter(Boolean);

      // Prepare jurisdiction snapshot (Phase 16B) - public save function
      const jurisdictionSnapshot = (state.jurisdictionIds || []).map(jurisdictionId => {
        const jurisdiction = state.availableJurisdictions.find(j => j.id === jurisdictionId);
        if (!jurisdiction) return null;
        return {
          id: jurisdiction.id,
          jurisdiction_type: jurisdiction.jurisdiction_type,
          jurisdiction_name: jurisdiction.jurisdiction_name,
          parent_jurisdiction: jurisdiction.parent_jurisdiction
        };
      }).filter(Boolean);

      // Calculate total rebate savings (fixed amounts only)
      const totalRebateSavings = selectedRebatesSnapshot.reduce((sum, rebate) => {
        if (rebate.amount_type === 'fixed') {
          return sum + parseFloat(rebate.amount);
        }
        return sum;
      }, 0);

      // Step 3: Save assessment
      const systemData = {
        age: state.systemAge,
        repairCost: state.repairCost,
        systemType: state.systemType,
        squareFootage: state.squareFootage,
        lastRepair: state.lastRepair,
        lastRepairCost: state.lastRepairCost,
        refrigerant: state.refrigerant,
        originalSeer: state.originalSeer,
        annualEnergyCost: state.annualEnergyCost,
        issues: state.issues,
        ductCondition: state.ductCondition,
        convertToHP: state.convertToHP,
        envelope: state.envelope,
        results: state.results
      };

      const { data: assessment, error: assessmentError } = await supabase
        .from('assessments')
        .insert({
          contractor_id: user.contractor_id,
          customer_id: customerId,
          technician_id: user.id,
          system_data: systemData,
          analysis_result: state.results,
          system_age: state.systemAge,
          repair_cost: state.repairCost,
          recommendation: state.results.recommendation,
          assessment_score: state.assessmentScore,
          electric_provider_id: state.electricProviderId || null,
          gas_provider_id: state.gasProviderId || null,
          jurisdiction_ids: state.jurisdictionIds && state.jurisdictionIds.length > 0 ? state.jurisdictionIds : null,
          jurisdiction_snapshot: jurisdictionSnapshot,
          selected_rebates_snapshot: selectedRebatesSnapshot,
          total_rebate_savings: totalRebateSavings
        })
        .select()
        .single();

      if (assessmentError) throw assessmentError;

      // Step 3: Increment contractor assessment count
      const { error: usageError } = await supabase.rpc('increment_assessment_count', {
        p_contractor_id: user.contractor_id
      });

      if (usageError) {
        console.error('Usage tracking error:', usageError);
        // Don't fail the save if usage tracking fails
      } else {
        // Refresh contractor data to update assessment count in Settings page
        if (refreshContractor) {
          await refreshContractor();
        }
      }

      dispatch({
        type: ActionTypes.SAVE_SUCCESS,
        assessmentId: assessment.id,
        customerId: customerId
      });

      return assessment.id;
    } catch (error) {
      console.error('Save assessment error:', error);
      dispatch({
        type: ActionTypes.SAVE_ERROR,
        error: error.message || 'Failed to save assessment'
      });
      return null;
    }
  }, [state, user, refreshContractor]);

  /**
   * Generate AI summary using OpenAI
   */
  const generateAISummary = useCallback(async () => {
    if (!state.results) {
      dispatch({
        type: ActionTypes.AI_GENERATION_ERROR,
        error: 'Please calculate results first'
      });
      return;
    }

    dispatch({ type: ActionTypes.START_AI_GENERATION });

    try {
      // Prepare assessment data for AI analysis
      const assessmentData = {
        age: state.systemAge,
        systemType: state.systemType,
        repairCost: state.repairCost,
        squareFootage: state.squareFootage,
        currentSeer: state.results.currentSeer,
        annualEnergyCost: state.annualEnergyCost,
        recommendation: state.results.recommendation,
        assessmentScore: state.results.assessmentScore,
        issues: state.issues,
        refrigerant: state.refrigerant,
        ductCondition: state.ductCondition
      };

      // Call Netlify Function
      const response = await fetch('/.netlify/functions/openai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assessmentData })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate AI summary');
      }

      const data = await response.json();

      dispatch({
        type: ActionTypes.AI_GENERATION_SUCCESS,
        summary: data.summary
      });

    } catch (error) {
      console.error('AI generation error:', error);
      dispatch({
        type: ActionTypes.AI_GENERATION_ERROR,
        error: error.message || 'Failed to generate AI insights. Please try again.'
      });
    }
  }, [state]);

  /**
   * Reset assessment to initial state
   */
  const reset = useCallback(() => {
    dispatch({ type: ActionTypes.RESET });
  }, []);

  /**
   * Check if we can proceed to next step
   */
  const canProceed = useMemo(() => {
    const validator = stepValidations[state.currentStep];
    if (validator) {
      const errors = validator(state);
      return Object.keys(errors).length === 0;
    }
    return true;
  }, [state]);

  /**
   * Get progress percentage
   */
  const progress = useMemo(() => {
    return ((state.currentStep - 1) / 5) * 100; // 0%, 20%, 40%, 60%, 80%, 100%
  }, [state.currentStep]);

  return {
    // State
    state,
    currentStep: state.currentStep,
    errors: state.errors,
    results: state.results,
    isCalculating: state.isCalculating,
    isSaving: state.isSaving,
    isGeneratingAI: state.isGeneratingAI,
    assessmentId: state.assessmentId,
    customerId: state.customerId,
    aiSummary: state.aiSummary,

    // Computed
    canProceed,
    progress,

    // Field setters
    setField,
    setEnvelopeField,
    setIssue,
    toggleIssue,

    // Navigation
    nextStep,
    prevStep,
    goToStep,

    // Validation
    validateCurrentStep,

    // Actions
    updateEnergyCostEstimate,
    calculateResults,
    saveAssessment,
    generateAISummary,
    reset
  };
}

/**
 * Helper hook for getting system type label
 */
export function useSystemTypeLabel(systemType) {
  const labels = {
    centralAC: 'Central AC',
    heatPump: 'Heat Pump',
    furnaceAC: 'Furnace + AC',
    ductlessMini: 'Ductless Mini-Split',
    furnaceOnly: 'Gas Furnace'
  };
  return labels[systemType] || systemType;
}

/**
 * Helper hook for getting refrigerant info
 */
export function useRefrigerantInfo(refrigerant) {
  const info = {
    r454b: 'Current EPA-approved standard with stable pricing and wide availability.',
    r410a: 'Phasing out 2025–2030. Prices likely to rise as production declines.',
    r22: 'Production ceased in 2020. Limited supply; repairs requiring R-22 can be very expensive.',
    unsure: 'Check the data plate on your outdoor unit for refrigerant type.'
  };
  return info[refrigerant] || '';
}

export default useAssessment;
